import React from "react";
import {
  Chip as MuiChip,
  ChipProps as MuiChipProps,
  styled,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  COLOR_CONSTANTS,
  SPACING_CONSTANTS,
  FONT_SIZES,
} from "../../constants/styles";

// Chip Appearance 타입 정의
export type ChipAppearance =
  | "disabled"
  | "default"
  | "map-chip"
  | "text"
  | "tag"
  | "error";

/**
 * 태그/레이블 칩 props — MUI `Chip` 의 color/variant 를 자체 `appearance` 토큰으로 교체.
 *
 * 좌/우 슬롯 아이콘을 지원하며, 우측 아이콘은 기본 X 아이콘 + 삭제 핸들러 패턴에 자주 쓴다.
 */
export interface ChipProps extends Omit<MuiChipProps, "color" | "variant"> {
  /**
   * Chip의 외관 스타일
   */
  appearance?: ChipAppearance;
  /**
   * 왼쪽 슬롯에 표시할 아이콘
   */
  leftIcon?: React.ReactNode;
  /**
   * 오른쪽 슬롯에 표시할 아이콘 (기본값: X 아이콘)
   */
  rightIcon?: React.ReactNode;
  /**
   * 오른쪽 아이콘 클릭 핸들러
   */
  onRightIconClick?: () => void;
  /**
   * 오른쪽 아이콘 표시 여부
   */
  showRightIcon?: boolean;
}

// Appearance별 스타일 정의 (Figma 디자인 정확히 반영)
const getAppearanceStyles = (appearance: ChipAppearance) => {
  switch (appearance) {
    case "disabled":
      return {
        backgroundColor: "transparent",
        border: `1px solid ${COLOR_CONSTANTS._components.chip.defaultEnabledBorder}`,
        color: COLOR_CONSTANTS.text.disabled,
        iconColor: COLOR_CONSTANTS.text.disabled,
      };
    case "default":
      return {
        backgroundColor: COLOR_CONSTANTS.common.white_states.main,
        border: `1px solid ${COLOR_CONSTANTS._components.chip.defaultEnabledBorder}`,
        color: COLOR_CONSTANTS.text.secondary,
        iconColor: COLOR_CONSTANTS.text.secondary,
      };
    case "map-chip":
      return {
        backgroundColor: "rgba(0, 0, 0, 0.56)", // 56% opacity - 특수 케이스
        border: "none",
        color: COLOR_CONSTANTS.common.white_states.main,
        iconColor: COLOR_CONSTANTS.common.white_states.main,
      };
    case "text":
      return {
        backgroundColor: "transparent",
        border: `1px solid ${COLOR_CONSTANTS._components.chip.defaultEnabledBorder}`,
        color: COLOR_CONSTANTS.text.secondary,
        iconColor: COLOR_CONSTANTS.text.secondary,
      };
    case "tag":
      return {
        backgroundColor: `${COLOR_CONSTANTS.primary.main}1A`, // 10% opacity (hex with alpha)
        border: `1px solid ${COLOR_CONSTANTS.primary.main}`,
        color: COLOR_CONSTANTS.COLOR.BLUE[600],
        iconColor: COLOR_CONSTANTS.COLOR.BLUE[600],
      };
    case "error":
      return {
        backgroundColor: `${COLOR_CONSTANTS.error.main}4D`, // 30% opacity (hex with alpha)
        border: `1px solid ${COLOR_CONSTANTS.error.main}`,
        color: COLOR_CONSTANTS.COLOR.RED[800],
        iconColor: COLOR_CONSTANTS.COLOR.RED[800],
      };
    default:
      return {
        backgroundColor: COLOR_CONSTANTS.common.white_states.main,
        border: `1px solid ${COLOR_CONSTANTS._components.chip.defaultEnabledBorder}`,
        color: COLOR_CONSTANTS.text.secondary,
        iconColor: COLOR_CONSTANTS.text.secondary,
      };
  }
};

// Styled Chip 컴포넌트
const StyledChip = styled(MuiChip)<{ appearance: ChipAppearance }>(
  ({ appearance }) => {
    const styles = getAppearanceStyles(appearance);

    return {
      height: "22px",
      borderRadius: SPACING_CONSTANTS.borderRadius.sm,
      fontFamily: "Pretendard",
      fontWeight: 500,
      fontSize: FONT_SIZES.sm,
      lineHeight: "18px",
      letterSpacing: "0.16px",
      padding: `0 ${SPACING_CONSTANTS.spacing[2]}px`, // Figma 기준으로 패딩 조정
      minWidth: "auto",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",

      // 기본 스타일
      backgroundColor: styles.backgroundColor,
      border: styles.border,
      color: styles.color,

      // 호버 상태
      "&:hover": {
        opacity: appearance === "disabled" ? 1 : 0.8,
      },

      // 포커스 상태
      "&:focus-visible": {
        outline: `2px solid ${COLOR_CONSTANTS.COLOR.BLUE[700]}`,
        outlineOffset: "2px",
      },

      // 비활성화 상태
      "&.Mui-disabled": {
        opacity: 1,
        cursor: "not-allowed",
      },

      // 라벨 스타일
      "& .MuiChip-label": {
        padding: "0",
        fontSize: FONT_SIZES.sm,
        fontWeight: 500,
        lineHeight: "18px",
        letterSpacing: "0.16px",
        color: styles.color,
      },

      // 아이콘 스타일 (왼쪽 아이콘)
      "& .MuiChip-icon": {
        fontSize: FONT_SIZES.sm,
        width: "12px",
        height: "12px",
        margin: `0 ${SPACING_CONSTANTS.spacing[1]}px 0 0`,
        color: styles.iconColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },

      // 삭제 아이콘 스타일 (오른쪽 아이콘)
      "& .MuiChip-deleteIcon": {
        fontSize: FONT_SIZES.lg,
        width: "16px",
        height: "16px",
        margin: `0 0 0 ${SPACING_CONSTANTS.spacing[1]}px`,
        cursor: "pointer",
        color: styles.iconColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:hover": {
          opacity: 0.7,
        },
      },

      // 아이콘이 없을 때의 패딩 조정
      "&:not(.MuiChip-icon) .MuiChip-label": {
        // padding: "0 8px",
      },
    };
  }
);

/**
 * Chip 컴포넌트
 *
 * 다양한 appearance와 slot을 지원하는 Chip 컴포넌트입니다.
 * Figma 디자인 시스템을 기반으로 구현되었습니다.
 */
export const Chip: React.FC<ChipProps> = ({
  appearance = "default",
  leftIcon,
  rightIcon,
  onRightIconClick,
  showRightIcon = false,
  label,
  children,
  disabled = false,
  ...props
}) => {
  // 오른쪽 아이콘 결정
  const finalRightIcon = showRightIcon ? rightIcon || <CloseIcon /> : undefined;

  // 오른쪽 아이콘 클릭 핸들러
  const handleDelete = () => {
    if (onRightIconClick && !disabled) {
      onRightIconClick();
    }
  };

  return (
    <StyledChip
      appearance={appearance}
      label={label || children}
      icon={leftIcon as React.ReactElement}
      deleteIcon={finalRightIcon as React.ReactElement}
      onDelete={showRightIcon ? handleDelete : undefined}
      disabled={disabled || appearance === "disabled"}
      {...props}
    />
  );
};

export default Chip;
